<template>
  <div class="container my-5">
    <div v-if="user" class="card p-4 shadow">
      <!-- Add Edit Button -->
      <div class="text-end mb-2">
        <button class="btn btn-sm btn-outline-secondary" @click="editMode = !editMode">
          {{ editMode ? "Cancel" : "Edit Profile" }}
        </button>
      </div>

      <!-- Editable Form -->
      <div v-if="editMode">
        <div class="mb-3">
          <label for="editName" class="form-label">Name</label>
          <input v-model="editName" id="editName" class="form-control" type="text" />
          <span class="text-danger">{{ verificationText }}</span>
        </div>

        <div class="mb-3">
          <label for="editAddress" class="form-label">Phone</label>
          <input v-model="editPhoneNumber" id="editPhoneNumber" class="form-control" type="text" />
        </div>

        <div class="mb-3">
          <label for="editAddress" class="form-label">Address</label>
          <input v-model="editAddress" id="editAddress" class="form-control" type="text" />
        </div>

        <button class="btn btn-primary" @click="updateProfile">Save Changes</button>
      </div>
      <div v-else>
        <!-- Avatar -->
        <div class="text-center mb-3">
          <img
            :src="user.avatarUrl ? `${BASE_IMAGE_PREFIX}/${user.avatarUrl}` : defaultAvatar"
            alt="User Avatar"
            class="rounded-circle"
            width="120"
            height="120"
          />
        </div>
  
        <h3 class="text-center">{{ user.name }}</h3>
        <p class="text-center text-muted">
          Status:
          <span
            :class="{
              'text-success': user.status === 'active',
              'text-warning': user.status === 'deactivated',
              'text-danger': user.status === 'banned',
            }"
          >
            {{ user.status }}
          </span>
        </p>
  
        <hr />
  
        <div>
          <p><strong>Email:</strong> {{ user.email }}</p>
  
          <p v-if="PHONE_VERIFICATION_ENABLED">
            {{ PHONE_VERIFICATION_ENABLED }}
            <strong>Phone:</strong>
            {{ user.phoneNumber ? `+${user.phoneNumber}` : "N/A" }}
            <span v-if="user.isPhoneVerified" class="text-primary ms-2">
              <i class="bi bi-patch-check-fill"></i>
            </span>
            <button
              v-else
              class="btn btn-sm btn-outline-primary ms-2"
              @click="startVerification()"
            >
              Verify Phone
            </button>
          </p>
          <p v-else>
            <strong>Phone:</strong>
            {{ user.phoneNumber ? `+${user.phoneNumber}` : "N/A" }}
          </p>
          <p><strong>Address:</strong> {{ user.address || "N/A" }}</p>
          <a class="btn btn-danger" href="#" @click.prevent="logout">Logout</a>
        </div>
  
        <hr />
      </div>
  
      <!-- Phone Verification Modal -->
      <div
        class="modal fade"
        tabindex="-1"
        :class="{ show: showPhoneModal }"
        style="display: block"
        v-if="showPhoneModal"
      >
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Verify Your Phone Number</h5>
              <button
                type="button"
                class="btn-close"
                @click="closePhoneModal"
              ></button>
            </div>
            <div v-if="addPhoneNumber" class="modal-body">
              <div class="input-group mb-3">
                <span class="input-group-text" id="basic-addon1">
                  +{{ phoneNumberPrefix }}
                </span>
                <input
                  type="text"
                  class="form-control"
                  v-model="verificationPhoneNumber"
                  placeholder="your phone number"
                />
              </div>
              <button class="btn btn-primary" @click="submitPhoneNumber">
                Send OTP
              </button>
              <p v-if="verificationText" class="text-danger mt-1">{{ verificationText }}</p>
            </div>
            <div v-else class="modal-body">
              <input
                type="text"
                class="form-control mb-3"
                v-model="verificationCode"
                placeholder="Enter verification code"
              />
              <button class="btn btn-primary" @click="verifyPhone">Submit</button>
              <p v-if="verificationText" class="mt-1">{{ verificationText }}</p>
            </div>
          </div>
        </div>
      </div>
      </div>
  </div>
</template>

<script>
import axios from "axios";
import {
  BASE_URL,
  PHONE_VERIFICATION_ENABLED,
  BASE_IMAGE_PREFIX,
} from "@/globals";

export default {
  name: "MyProfile",
  data() {
    return {
      BASE_IMAGE_PREFIX,
      user: {},
      ordersList: [],
      isPhoneVerified: false,
      showPhoneModal: false,
      verificationCode: null,
      verificationText: "",
      addPhoneNumber: false,
      verificationPhoneNumber: null,
      phoneNumberPrefix: "880",
      editMode: false,
      editName: "",
      editAddress: "",
      editPhoneNumber: "",
      PHONE_VERIFICATION_ENABLED,
      defaultAvatar:
        "https://static.vecteezy.com/system/resources/previews/026/966/960/non_2x/default-avatar-profile-icon-of-social-media-user-vector.jpg",
    };
  },
  mounted() {
    this.fetchUserProfile();
    this.checkPhoneVerification();
  },
  methods: {
    async fetchUserProfile() {
      try {
        this.verificationText = ""
        const token = this.$store.getters.token;
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${BASE_URL}/api/users/user`, {
          headers,
        });
        if (response.data?.reason === 'INVALID_TOKEN') {
          this.$store.dispatch("logout");
          setTimeout(() => {
            this.$router.push('/login')
          }, 300);
        }
        this.user = response.data;
        this.editName = this.user.name;
        this.editAddress = this.user.address;
        this.editPhoneNumber = this.user.phoneNumber;
      } catch (error) {
        if (error.response.data?.reason === 'INVALID_TOKEN') {
          this.$store.dispatch("logout");
          setTimeout(() => {
            this.$router.push('/login')
          }, 300);
        }
        // this.$router.push("/");
      }
    },
    async updateProfile() {
      if (!this.editName.trim()) {
        this.verificationText = "Name cannot be empty";
        return;
      }

      try {
        const token = this.$store.getters.token;
        const headers = { Authorization: `Bearer ${token}` };

        const response = await axios.patch(`${BASE_URL}/api/users/user`, {
          name: this.editName,
          address: this.editAddress,
          phoneNumber: this.editPhoneNumber,
        }, { headers });

        this.verificationText = response.data.message || "Profile updated!";
        this.editMode = false;
        this.fetchUserProfile();
      } catch (error) {
        this.verificationText = "Failed to update profile.";
      }
    },
    async checkPhoneVerification() {
      if (PHONE_VERIFICATION_ENABLED) {
        const token = this.$store.getters.token;
        const headers = {
          Authorization: `Bearer ${token}`,
        };
        try {
          const response = await axios.get(
            `${BASE_URL}/api/users/user-phone-verified`,
            { headers }
          );
          if (response.data.status) {
            this.isPhoneVerified = response.data.status;
            this.showPhoneModal = false;
          }
        } catch (error) {
          this.errorMessage = error.response.data.message;
        }
      }
    },
    startVerification() {
      this.addPhoneNumber = true;
      this.showPhoneModal = true;
    },
    async submitPhoneNumber() {
      const isValid = /^\d{10}$/.test(this.verificationPhoneNumber);

      if (!isValid) {
        this.verificationText = "Phone number must be exactly 10 digits"
        return;
      }

      try {
        const token = this.$store.getters.token;
        const headers = { Authorization: `Bearer ${token}` };

        const response = await axios.post(
          `${BASE_URL}/api/users/otp-generate`,
          {
            phoneNumber: `${this.phoneNumberPrefix}${this.verificationPhoneNumber}`,
          },
          { headers }
        );
        if (response.data.status) {
          this.addPhoneNumber = false;
          this.verificationText = ""
        }
      } catch (error) {
        this.verificationText = "Verification failed. Please try again.";
      }
    },
    async verifyPhone() {
      try {
        const token = this.$store.getters.token;
        const headers = { Authorization: `Bearer ${token}` };

        const response = await axios.post(
          `${BASE_URL}/api/users/verify-phone-number`,
          { otp: this.verificationCode },
          { headers }
        );

        if (response.data.status) {
          this.verificationText = response.data.message;
          this.isPhoneVerified = true;
          this.fetchUserProfile()
          setTimeout(() => {
            this.closePhoneModal();
            this.verificationText = ""
          }, 5000);
        } else {
          this.verificationText = "OTP verification failed. Please try again.";
        }
      } catch (error) {
        this.verificationText = "OTP verification failed. Please try again.";
      }
    },
    closePhoneModal() {
      this.showPhoneModal = false;
      this.verificationCode = "";
    },
    logout() {
      this.$store.dispatch("logout");
      window.location.reload();
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  max-width: 600px;
  margin: auto;
}
img {
  object-fit: cover;
}
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
.modal.show {
  display: block;
}
</style>

